import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import knowtexLogo from ".././assets/White Knowtex Logo.png";
import Pool from "../UserPool";
import { isAnyCapabilityEnabled } from "../config/capabilities";

function Sidebar({ setAuth, setUserOrg }) {
  const EXPANDED_WIDTH = 250;
  const COLLAPSED_WIDTH = 80;
  const { pathname } = useLocation();
  const sidebarRef = useRef(null);
  const [isCollapsed, setIsCollapsed] = useState(
    () => localStorage.getItem("sidebarCollapsed") === "true",
  );
  const [openDropdown, setOpenDropdown] = useState(null);

  const toggleDropdown = (category) => {
    setOpenDropdown((prev) => (prev === category ? null : category));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const width = isCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;
    document.documentElement.style.setProperty("--sidebar-width", `${width}px`);
    localStorage.setItem("sidebarCollapsed", String(isCollapsed));
  }, [isCollapsed]);

  const logoutUser = () => {
    const user = Pool.getCurrentUser();
    if (user) user.signOut();
    localStorage.removeItem("token");
    setUserOrg(null);
    setAuth(false);
  };

  const isPathActive = (path) =>
    pathname === path || (path !== "/" && pathname.startsWith(`${path}/`));

  const isCategoryActive = (paths) => paths.some((path) => isPathActive(path));

  const iconClass = "w-[20px] h-[20px]";

  const menuCategories = [
    {
      key: "engineering",
      label: "Engineering",
      icon: (
        <svg
          className="w-[30px] h-[30px]"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            stroke="white"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M10 3v4a1 1 0 0 1-1 1H5m5 4-2 2 2 2m4-4 2 2-2 2m5-12v16a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7.914a1 1 0 0 1 .293-.707l3.914-3.914A1 1 0 0 1 9.914 3H18a1 1 0 0 1 1 1Z"
          />
        </svg>
      ),
      items: [
        { path: "/oncall", label: "Oncall", capability: "oncall" },
        { path: "/add-test-patient", label: "Add Test Patient", capability: "addTestPatient" },
        { path: "/support", label: "Customer Support", capability: "customerSupport" },
        { path: "/templates", label: "Templates", capability: "templates" },
        { path: "/workflows", label: "Workflows", capability: "workflows" },
        { path: "/workflows/visualization", label: "Workflows Viz", capability: "workflowsVisualization" },
        { path: "/custom-dictionary", label: "Custom Dictionary", capability: "customDictionary" },
        { path: "/transcription-rules", label: "Transcription Rules", capability: "transcriptionRules" },
        { path: "/review-note", label: "Review Note", capability: "reviewNote" },
        { path: "/review-transcript", label: "Review Transcript", capability: "reviewTranscript" },
        { path: "/summarize", label: "All Jobs", capability: "allJobs" },
        { path: "/coding", label: "Coding", capability: "coding" },
        { path: "/fine-tuning", label: "ML Workflows", capability: "mlWorkflows" },
        { path: "/evaluation", label: "Evaluation", capability: "evaluation" },
        { path: "/qa-eval", label: "QA Eval", capability: "qaEval" },
        { path: "/va-eval", label: "VA Eval", capability: "vaEval" },
        { path: "/patient-management", label: "Patient Management", capability: "patientManagement" },
        { path: "/hallucination", label: "Hallucination", capability: "hallucination" },
        { path: "/argus", label: "Argus", capability: "argus" },
        { path: "/mc", label: "MC", capability: "mc" },
      ],
    },
    {
      key: "operations",
      label: "Operations",
      icon: (
        <svg
          className="w-[30px] h-[30px]"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            stroke="white"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M4 19V5m0 14h16M8 17v-5m4 5V9m4 8V7"
          />
        </svg>
      ),
      items: [
        { path: "/internal-external-dashboard", label: "External View", capability: "externalView" },
        { path: "/register-doctors", label: "Register Doctors", capability: "registerDoctors" },
        { path: "/update-doctors", label: "Update Doctors", capability: "updateDoctors" },
        { path: "/va-register", label: "VA Registration", capability: "vaRegister" },
        { path: "/cs-tools", label: "CS Tools", capability: "csTools" },
        { path: "/payment-reconcile", label: "Payment Reconcile", capability: "paymentReconcile" },
        { path: "/revops", label: "Revenue Operations", capability: "revops" },
        { path: "/financial-review", label: "Financial Review", capability: ["financialReview", "commercialFinancialReview"] },
      ],
    },
    {
      key: "analytics",
      label: "Analytics",
      icon: (
        <svg
          className="w-[30px] h-[30px]"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            stroke="white"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 0 1-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8Z"
          />
        </svg>
      ),
      items: [
        { path: "/user-feedback", label: "User Feedback", capability: "userFeedback" },
        { path: "/eval", label: "Model Eval", capability: "modelEval" },
        { path: "/external-usage", label: "External Usage", capability: "externalUsage" },
      ],
    },
    {
      key: "integrations",
      label: "Integrations",
      icon: (
        <svg className="w-[30px] h-[30px]" fill="none" viewBox="0 0 24 24"
            stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round">
          <rect x="3"  y="3"  width="7" height="7" rx="1" />
          <rect x="14" y="3"  width="7" height="7" rx="1" />
          <rect x="3"  y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
      ),
      items: [
        { path: "/integrations", label: "Overview", capability: "integrations" },
        { path: "/oncoemr", label: "OncoEMR", capability: "oncoemr" },
        { path: "/boomerang", label: "Boomerang", capability: "boomerang" },
      ],
    },
    {
      key: "customer-delivery",
      label: "Customer Delivery",
      icon: (
        <svg
          className="w-[30px] h-[30px]"
          aria-hidden="true"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M13 7h6l2 4m-8-4v8m0-8V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v9h2m8 0H9m4 0h2m4 0h2v-4m0 0h-5"
          />
        </svg>
      ),
      items: [
        { path: "/karoline", label: "KAROLINE", capability: "karoline" },
        { path: "/foia", label: "FOIA Requests", capability: "foiaTracking" },
      ],
    },
  ];

  const visibleMenuCategories = menuCategories
    .map((category) => ({
      ...category,
      items: category.items.filter((item) =>
        isAnyCapabilityEnabled(item.capability)
      ),
    }))
    .filter((category) => category.items.length > 0);

  return (
    <div
      ref={sidebarRef}
      className="fixed top-0 left-0 z-10 flex flex-col overflow-visible h-screen bg-purple2 transition-all duration-200"
      style={{ width: `${isCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH}px` }}
    >
      <div className="pt-3 px-3 flex items-center justify-between">
        <button
          type="button"
          onClick={() => {
            setIsCollapsed((prev) => !prev);
            setOpenDropdown(null);
          }}
          className="text-white hover:bg-dark-purple rounded-md p-2"
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        {!isCollapsed && (
          <Link to="/" className="pl-2 pr-2">
            <img alt="knowtex-logo" src={knowtexLogo} />
          </Link>
        )}
      </div>

      <div
        className={`flex flex-col flex-1 items-center justify-start overflow-y-auto overflow-x-visible pb-4 ${
          isCollapsed ? "pt-4" : "pt-8"
        }`}
      >
        <ul className="w-full space-y-4 flex flex-col overflow-x-visible">
          {visibleMenuCategories.map((category) => {
            const categoryPaths = category.items.map((item) => item.path);
            const isActiveCategory = isCategoryActive(categoryPaths);
            const isOpen = openDropdown === category.key;
            // Prefer the longest matching path so nested routes (e.g.
            // /workflows/visualization) resolve to their own row rather than the
            // parent (/workflows).
            const activeItem = category.items
              .filter((item) => isPathActive(item.path))
              .sort((a, b) => b.path.length - a.path.length)[0];

            return (
              <li key={category.key} className="font-light text-sm text-white relative flex flex-col items-center">
                <button
                  onClick={() => toggleDropdown(category.key)}
                  className={`flex items-center ${isCollapsed ? "justify-center w-12 h-12" : "w-[220px] px-3 py-1"} ${
                    isActiveCategory ? "font-bold" : "hover:underline"
                  }`}
                  title={category.label}
                >
                  {category.icon}
                  {!isCollapsed && (
                    <>
                      <div className="pl-4 text-left leading-tight">
                        <p className={`${isActiveCategory ? "font-bold text-[22px]" : "text-[20px]"}`}>
                          {category.label}
                        </p>
                        {activeItem && (
                          <p className="text-[14px] text-white opacity-90 underline mt-1 font-semibold">
                            {activeItem.label}
                          </p>
                        )}
                      </div>
                      <svg
                        className={`w-4 h-4 ml-auto transition-transform ${isOpen ? "rotate-180" : ""}`}
                        fill="white"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </>
                  )}
                </button>

                {isOpen && (
                  <div
                    className={`w-[220px] overflow-y-auto bg-purple2 shadow-knowtex-shadow rounded-lg z-[60] ${
                      isCollapsed
                        ? "fixed"
                        : "absolute top-full left-1/2 -translate-x-1/2 mt-1"
                    }`}
                    style={
                      isCollapsed
                        ? {
                            left: `${COLLAPSED_WIDTH + 8}px`,
                            top: "8px",
                            maxHeight: "calc(100vh - 16px)",
                          }
                        : { maxHeight: "70vh" }
                    }
                  >
                    <ul className="py-2 space-y-1">
                      {category.items.map((item) => {
                        const isActiveItem = item.path === activeItem?.path;
                        return (
                          <li key={item.path} className="px-2">
                            <Link
                              to={item.path}
                              className={
                                isActiveItem
                                  ? "flex items-center py-2 px-3 rounded-md bg-dark-purple border border-white/40 text-white shadow-sm"
                                  : "hover:underline hover:bg-transparent-purple flex items-center py-2 px-3 rounded-md transition-colors duration-200 text-white"
                              }
                            >
                              <svg
                                className={iconClass}
                                aria-hidden="true"
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  stroke="currentColor"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M4 19V5m0 14h16M8 17v-5m4 5V9m4 8V7"
                                />
                              </svg>
                              <p
                                className={
                                  isActiveItem
                                    ? "font-bold text-[18px] underline pl-3 text-white"
                                    : "text-[16px] pl-3"
                                }
                              >
                                {item.label}
                              </p>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <div className="flex justify-center items-center py-4 shrink-0">
        <button
          className={`text-white font-medium rounded-lg hover:bg-ktx-navy bg-dark-purple ${
            isCollapsed ? "p-3 text-[14px]" : "pt-4 pb-4 p-8 text-[20px]"
          }`}
          onClick={() => logoutUser()}
          title="Logout"
        >
          {isCollapsed ? (
            <svg
              className="w-5 h-5"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M16 12H4m12 0-3 3m3-3-3-3m7-4v14a1 1 0 0 1-1 1h-3"
              />
            </svg>
          ) : (
            <span className="inline-flex items-center gap-2">
              <svg
                className="w-5 h-5"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M16 12H4m12 0-3 3m3-3-3-3m7-4v14a1 1 0 0 1-1 1h-3"
                />
              </svg>
              Logout
            </span>
          )}
        </button>
      </div>
    </div>
  );
}

export default Sidebar;
